import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useT } from '../i18n/useT';
import { useLanguage } from '../i18n/LanguageContext';
import './ContactPage.css';

// PLACEHOLDER: Update form endpoint, company contact info, and privacy wording before launch

export default function ContactPage() {
  const t = useT();
  const { lang } = useLanguage();
  const p = t.pages.contact;

  const CONTACT_METHODS = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <path d="M3 5h16v12a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M3 5l8 8 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      ),
      label: lang === 'de' ? 'E-Mail-Support' : 'Email Support',
      value: '[support@yourdomain.com — PLACEHOLDER]',
      sub: lang === 'de' ? 'Antwortet typischerweise innerhalb von 24 Stunden' : 'Typically replies within 24 hours',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M11 7v4l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      label: lang === 'de' ? 'Antwortzeit' : 'Response Time',
      value: lang === 'de' ? 'Innerhalb von 24 Stunden' : 'Within 24 hours',
      sub: lang === 'de' ? 'Mo.–Fr., [Geschäftszeiten]' : 'Mon–Fri, [business hours — PLACEHOLDER]',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ),
      label: lang === 'de' ? 'Eingetragener Sitz' : 'Registered Office',
      value: '[Company Address — PLACEHOLDER]',
      sub: '[City, Country — PLACEHOLDER]',
    },
  ];

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formState.name.trim()) errs.name = lang === 'de' ? 'Bitte gib deinen Namen ein.' : 'Please enter your name.';
    if (!formState.email.trim() || !/\S+@\S+\.\S+/.test(formState.email)) errs.email = lang === 'de' ? 'Bitte gib eine gültige E-Mail-Adresse ein.' : 'Please enter a valid email address.';
    if (!formState.message.trim() || formState.message.trim().length < 10) errs.message = lang === 'de' ? 'Die Nachricht muss mindestens 10 Zeichen enthalten.' : 'Message must be at least 10 characters.';
    if (!formState.consent) errs.consent = lang === 'de' ? 'Du musst der Datenschutzerklärung zustimmen.' : 'You must agree to the privacy policy to submit.';
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    // PLACEHOLDER: Replace with actual form submission logic
    console.log('[PLACEHOLDER] Form submitted:', formState);
    setSubmitted(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const subjects = lang === 'de'
    ? [
        { value: '', label: 'Thema wählen…' },
        { value: 'support', label: 'Technischer Support' },
        { value: 'privacy', label: 'Datenschutz & Datenanfrage' },
        { value: 'sales', label: 'Vertrieb & Business-Anfrage' },
        { value: 'press', label: 'Presse & Medien' },
        { value: 'other', label: 'Sonstiges' },
      ]
    : [
        { value: '', label: 'Select a topic…' },
        { value: 'support', label: 'Technical Support' },
        { value: 'privacy', label: 'Privacy & Data Request' },
        { value: 'sales', label: 'Sales & Business Inquiry' },
        { value: 'press', label: 'Press & Media' },
        { value: 'other', label: 'Other' },
      ];

  return (
    <>
      <Helmet>
        <title>{p.title} – Docudi</title>
        <meta name="description" content={p.sub} />
        <link rel="canonical" href="https://[YOUR-DOMAIN.com]/contact" />
      </Helmet>

      <section className="page-hero">
        <div className="container">
          <div className="page-hero__content text-center">
            <div className="badge">{p.title}</div>
            <h1 className="page-hero__title">{p.headline}</h1>
            <p className="page-hero__sub">{p.sub}</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact__layout">
            {/* Contact methods */}
            <aside className="contact__info">
              {CONTACT_METHODS.map((method, i) => (
                <div key={i} className="contact__method">
                  <div className="contact__method-icon">{method.icon}</div>
                  <div>
                    <div className="contact__method-label">{method.label}</div>
                    <div className="contact__method-value">{method.value}</div>
                    <div className="contact__method-sub">{method.sub}</div>
                  </div>
                </div>
              ))}

              <div className="contact__note">
                <strong>{lang === 'de' ? 'Datenschutzanfragen:' : 'Privacy inquiries:'}</strong>{' '}
                {lang === 'de'
                  ? 'Für Anfragen betroffener Personen, Widerruf der Einwilligung oder DSGVO-Fragen bitte im Betreff angeben.'
                  : 'For data subject requests, consent withdrawal, or GDPR-related questions, please indicate this in your message subject.'}
              </div>
            </aside>

            {/* Contact form */}
            <div className="contact__form-wrap">
              {submitted ? (
                <div className="contact__success">
                  <div className="contact__success-icon" aria-hidden="true">✅</div>
                  <h2>{lang === 'de' ? 'Nachricht gesendet!' : 'Message sent!'}</h2>
                  <p>{lang === 'de' ? 'Danke für deine Nachricht. Wir melden uns innerhalb von 24 Stunden.' : "Thank you for reaching out. We'll get back to you within 24 hours."}</p>
                </div>
              ) : (
                <form className="contact__form" onSubmit={handleSubmit} noValidate>
                  <h2 className="contact__form-title">{lang === 'de' ? 'Nachricht senden' : 'Send a message'}</h2>

                  <div className="form-group">
                    <label htmlFor="contact-name" className="form-label">
                      {lang === 'de' ? 'Dein Name' : 'Your name'} <span className="required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      className={`form-input ${errors.name ? 'form-input--error' : ''}`}
                      placeholder="Jane Smith"
                      autoComplete="name"
                      aria-required="true"
                      aria-describedby={errors.name ? 'contact-name-error' : undefined}
                    />
                    {errors.name && <span id="contact-name-error" className="form-error" role="alert">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact-email" className="form-label">
                      {lang === 'de' ? 'E-Mail-Adresse' : 'Email address'} <span className="required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'form-input--error' : ''}`}
                      placeholder="jane@example.com"
                      autoComplete="email"
                      aria-required="true"
                      aria-describedby={errors.email ? 'contact-email-error' : undefined}
                    />
                    {errors.email && <span id="contact-email-error" className="form-error" role="alert">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact-subject" className="form-label">
                      {lang === 'de' ? 'Betreff' : 'Subject'}
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      value={formState.subject}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {subjects.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact-message" className="form-label">
                      {lang === 'de' ? 'Nachricht' : 'Message'} <span className="required" aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      className={`form-textarea ${errors.message ? 'form-input--error' : ''}`}
                      placeholder={lang === 'de' ? 'Beschreibe dein Anliegen…' : 'Describe your question or issue in detail…'}
                      rows={5}
                      aria-required="true"
                      aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    />
                    {errors.message && <span id="contact-message-error" className="form-error" role="alert">{errors.message}</span>}
                  </div>

                  {/* Privacy consent */}
                  <div className="form-group">
                    <div className="form-checkbox-group">
                      <input
                        id="contact-consent"
                        type="checkbox"
                        name="consent"
                        checked={formState.consent}
                        onChange={handleChange}
                        className="form-checkbox"
                        aria-required="true"
                        aria-describedby={errors.consent ? 'contact-consent-error' : 'contact-consent-hint'}
                      />
                      <label htmlFor="contact-consent" className="form-label" style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                        {lang === 'de'
                          ? 'Ich stimme zu, dass meine übermittelten Daten zur Bearbeitung meiner Anfrage verwendet werden gemäß der '
                          : 'I agree that my submitted data will be used to respond to my inquiry in accordance with the '}
                        <a href="/privacy" className="contact__form-link">
                          {lang === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
                        </a>.
                      </label>
                    </div>
                    {errors.consent && (
                      <span id="contact-consent-error" className="form-error" role="alert">{errors.consent}</span>
                    )}
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                    {lang === 'de' ? 'Nachricht senden' : 'Send Message'}
                  </button>

                  <p className="contact__form-footer">
                    ⚠️ {lang === 'de' ? 'Formularversand ist ein Platzhalter. Vor dem Launch mit Backend / E-Mail-Service verbinden.' : 'Form submission is a placeholder. Connect to your backend / email service before launch.'}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
